import React, { useRef, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import DOMPurify from 'dompurify';
import 'react-quill/dist/quill.snow.css';
import './FormattingToolbar.css';
import { FormattedText } from '../types/models';

interface FormattingToolbarProps {
  value: FormattedText;
  onChange: (value: FormattedText) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  id?: string;
  required?: boolean;
  disabled?: boolean;
}

/**
 * FormattingToolbar component for rich text editing with bold and italic formatting
 * Integrates with ReactQuill for rich text editing and generates sanitized HTML output
 * Requirements: 8.2, 8.5
 */
const FormattingToolbar: React.FC<FormattingToolbarProps> = ({
  value,
  onChange,
  placeholder = 'Enter text with formatting...',
  label,
  className = '',
  id,
  required = false,
  disabled = false
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const toolbarId = id ? `${id}-toolbar` : `toolbar-${Math.random().toString(36).substr(2, 9)}`;

  /**
   * Quill modules configuration
   * Only enable bold and italic formatting as per requirements
   */
  const modules = useMemo(() => ({
    toolbar: {
      container: `#${toolbarId}`,
      handlers: {
        // Custom handlers can be added here if needed
      }
    },
    clipboard: {
      // Strip formatting on paste except bold and italic
      matchVisual: false
    }
  }), [toolbarId]);

  /**
   * Quill formats configuration
   * Only allow bold and italic formatting
   */
  const formats = ['bold', 'italic'];

  /**
   * Sanitize HTML to prevent XSS attacks
   * Only allow bold and italic tags
   */
  const sanitizeHtml = (html: string): string => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ['strong', 'em', 'b', 'i', 'p', 'br'],
      ALLOWED_ATTR: []
    });
  };

  /**
   * Extract plain text from HTML
   */
  const extractPlainText = (html: string): string => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  /**
   * Handle content change from Quill editor
   */
  const handleChange = (content: string, delta: any, source: any, editor: any) => {
    const html = content;
    const plainText = editor.getText();
    
    // Sanitize HTML output
    const sanitizedHtml = sanitizeHtml(html);
    
    // Create FormattedText object
    const formattedText: FormattedText = {
      plainText: plainText.trim(),
      html: sanitizedHtml
    };
    
    onChange(formattedText);
  };

  const inputId = id || `formatting-toolbar-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={`formatting-toolbar-container ${className}`}>
      {label && (
        <label htmlFor={inputId} className="formatting-toolbar-label">
          {label}
          {required && <span className="required-indicator">*</span>}
        </label>
      )}

      {/* Custom toolbar */}
      <div id={toolbarId} className="formatting-toolbar">
        <button className="ql-bold" title="Bold" type="button" disabled={disabled}>
          <strong>B</strong>
        </button>
        <button className="ql-italic" title="Italic" type="button" disabled={disabled}>
          <em>I</em>
        </button>
      </div>

      {/* Quill editor */}
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value.html || ''}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={disabled}
        className={`formatting-editor ${disabled ? 'disabled' : ''}`}
      />

      {/* Validation feedback */}
      {required && !value.plainText && (
        <div className="validation-message error">
          This field is required
        </div>
      )}
    </div>
  );
};

export default FormattingToolbar;
